import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { FieldTestService } from '../../../../../core/services/field-test.service';
import { DroneService } from '../../../../../core/services/drone.service';
import { CustomFieldTest, FieldTestFile } from '../../../../../core/models/field-test.model';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { toCdnUrl } from 'src/app/shared/utils/format.utils';

@Component({
  selector: 'app-custom-field-test-details',
  standalone: true,
  imports: [CommonModule, RouterLink, ConfirmModalComponent],
  templateUrl: './custom-field-test-details.component.html',
  styleUrls: ['./custom-field-test-details.component.scss']
})
export class CustomFieldTestDetailsComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fieldTestService = inject(FieldTestService);
  private droneService = inject(DroneService);

  // State
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);
  testId = signal<string | null>(null);
  test = signal<CustomFieldTest | null>(null);

  ngOnInit(): void {
    // Load drones for name resolution
    this.droneService.loadOrganizationDrones();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.testId.set(id);
      this.loadTest(id);
    } else {
      this.errorMessage.set('No test ID provided');
      this.isLoading.set(false);
    }
  }

  private async loadTest(id: string): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const found = await this.fieldTestService.fetchCustomTestById(id);
      if (found) {
        this.test.set(found);
      } else {
        this.errorMessage.set('Custom field test not found');
      }
    } catch (error) {
      console.error('Error loading custom field test:', error);
      this.errorMessage.set('Failed to load custom field test');
    } finally {
      this.isLoading.set(false);
    }
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/logbook/testing/field']);
  }

  editTest(): void {
    const id = this.testId();
    if (id) {
      this.router.navigate(['/logbook/testing/field/custom', id, 'edit']);
    }
  }

  // Delete functionality
  confirmDelete(): void {
    const t = this.test();
    if (t) {
      this.confirmModal.open({
        title: 'Delete Custom Field Test',
        message: `Are you sure you want to delete "${t.title}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonClass: 'btn-danger',
        icon: 'bi-trash'
      });
    }
  }

  async onDeleteConfirmed(): Promise<void> {
    const id = this.testId();
    if (id) {
      const success = await this.fieldTestService.deleteCustomTest(id);
      if (success) {
        this.router.navigate(['/logbook/testing/field']);
      }
    }
  }

  onDeleteCancelled(): void {
    // No action needed
  }

  /**
   * Open file in new tab via CDN with signed URL query params
   */
  async openFile(file: FieldTestFile): Promise<void> {
    if (!file.filePath) {
      console.error('No file path available');
      return;
    }

    try {
      const signedUrl = await this.fieldTestService.getDownloadUrl(file.filePath);
      if (signedUrl) {
        window.open(toCdnUrl(signedUrl), '_blank');
      } else {
        console.error('Failed to get download URL');
      }
    } catch (error) {
      console.error('Error opening file:', error);
    }
  }

  // Helper methods
  getTestTypeIcon(type: string): string {
    switch (type) {
      case 'nozzle-calibration': return 'bi-droplet-half';
      case 'equipment-inspection': return 'bi-clipboard-check';
      case 'spray-pattern': return 'bi-grid-3x3';
      case 'flow-rate': return 'bi-speedometer2';
      case 'tank-calibration': return 'bi-bucket';
      case 'other': return 'bi-file-earmark';
      default: return 'bi-file-earmark';
    }
  }

  getTestTypeLabel(type: string): string {
    switch (type) {
      case 'nozzle-calibration': return 'Nozzle Calibration';
      case 'equipment-inspection': return 'Equipment Inspection';
      case 'spray-pattern': return 'Spray Pattern Test';
      case 'flow-rate': return 'Flow Rate Verification';
      case 'tank-calibration': return 'Tank Calibration';
      case 'other': return 'Other';
      default: return type;
    }
  }

  getCategoryLabel(category: string): string {
    const labels: Record<string, string> = {
      'pre-season': 'Pre-Season',
      'in-season': 'In-Season',
      'post-season': 'Post-Season',
      'maintenance': 'Maintenance'
    };
    return labels[category] || category || 'Not specified';
  }

  getFrequencyLabel(frequency: string): string {
    const labels: Record<string, string> = {
      'as-needed': 'As Needed',
      'every-flight': 'Every Flight',
      'daily': 'Daily',
      'weekly': 'Weekly',
      'monthly': 'Monthly',
      'annually': 'Annually'
    };
    return labels[frequency] || frequency || 'Not specified';
  }

  getAircraftDisplayName(applicableAircraft: string): string {
    if (!applicableAircraft || applicableAircraft === 'all') {
      return 'All Aircraft';
    }

    const drone = this.droneService.getDroneById(applicableAircraft);
    if (drone) {
      return drone.name;
    }

    return applicableAircraft;
  }

  getFileIcon(type: string): string {
    if (type === 'application/pdf' || type === 'pdf') return 'bi-file-earmark-pdf';
    if (type.startsWith('image/') || ['jpg', 'jpeg', 'png'].includes(type)) return 'bi-file-earmark-image';
    if (type.includes('word') || ['doc', 'docx'].includes(type)) return 'bi-file-earmark-word';
    return 'bi-file-earmark';
  }

  formatDate(date: string): string {
    if (!date) return 'Not specified';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
